(() => {
  function requestId() {
    return crypto.randomUUID().replaceAll('-', '');
  }

  function readPortalCredentials() {
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const attempted = params.has('ticket') || params.has('key');
    if (!attempted) return null;
    const ticket = params.get('ticket') || '';
    const key = params.get('key') || '';
    history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    const validTicket = /^MT-\d{8}-[A-Z0-9]{5,12}$/.test(ticket);
    const validKey = /^[A-Za-z0-9_-]{20,100}$/.test(key);
    return validTicket && validKey ? { ticket, key } : { invalid: true };
  }

  const portalCredentials = readPortalCredentials();

  function showFormErrors(root, form, errors = {}) {
    let first = null;
    for (const [name, text] of Object.entries(errors)) {
      const target = root.querySelector(`[data-support-error="${CSS.escape(name)}"]`);
      if (target) target.textContent = text;
      const input = form.elements[name];
      if (input && name !== 'form') {
        input.setAttribute('aria-invalid', 'true');
        first ||= input;
      }
    }
    (first || root.querySelector('[data-support-error="form"]'))?.focus?.();
  }

  function initTicketPortal(root, credentials) {
    if (root.dataset.ticketPortalReady === 'true') return;
    root.dataset.ticketPortalReady = 'true';
    root.classList.add('native-support--portal-active');
    root.querySelector('.native-support__intro').hidden = true;
    root.querySelector('[data-support-create]').hidden = true;
    const portal = root.querySelector('[data-ticket-portal]');
    const state = portal.querySelector('[data-ticket-state]');
    const meta = portal.querySelector('[data-ticket-meta]');
    const messages = portal.querySelector('[data-ticket-messages]');
    const reply = portal.querySelector('[data-ticket-reply]');
    const error = portal.querySelector('[data-ticket-error]');
    const apiBase = root.dataset.apiBase || '';
    portal.hidden = false;
    portal.focus();

    if (credentials.invalid) {
      portal.querySelector('[data-ticket-reference]').textContent = 'Private link unavailable';
      state.textContent = 'This private link is incomplete or invalid. Please create a new ticket.';
      return;
    }

    const endpoint = `${apiBase}/api/tickets/${encodeURIComponent(credentials.ticket)}`;
    const authorization = { authorization: `Bearer ${credentials.key}` };

    function renderMessages(items = []) {
      messages.replaceChildren();
      for (const item of items) {
        const article = document.createElement('article');
        article.className = `native-support__message native-support__message--${item.author_type === 'staff' ? 'staff' : 'customer'}`;
        const heading = document.createElement('h3');
        heading.textContent = item.author_type === 'staff' ? 'Mintrove support' : 'You';
        const time = document.createElement('time');
        time.dateTime = item.created_at || '';
        time.textContent = item.created_at ? new Date(item.created_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
        const body = document.createElement('p');
        body.textContent = item.body || '';
        article.append(heading, time, body);
        messages.append(article);
      }
    }

    async function loadTicket() {
      state.textContent = 'Loading your ticket…';
      const response = await fetch(endpoint, { headers: authorization, credentials: 'omit' });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'This private link is no longer valid.');
      portal.querySelector('[data-ticket-reference]').textContent = result.ticket.id;
      portal.querySelector('[data-ticket-status]').textContent = result.ticket.statusLabel;
      portal.querySelector('[data-ticket-subject]').textContent = result.ticket.subject;
      meta.hidden = false;
      renderMessages(result.messages);
      reply.hidden = false;
      state.textContent = 'Your ticket is securely loaded.';
    }

    reply.addEventListener('submit', async (event) => {
      event.preventDefault();
      error.textContent = '';
      const button = reply.querySelector('button[type="submit"]');
      const textarea = reply.elements.message;
      button.disabled = true;
      state.textContent = 'Sending your reply…';
      try {
        const response = await fetch(`${endpoint}/messages`, {
          method: 'POST',
          headers: { ...authorization, 'content-type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({ message: textarea.value, requestId: requestId() }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || result.errors?.message || 'Your reply could not be sent.');
        textarea.value = '';
        await loadTicket();
        state.textContent = 'Your reply was safely stored.';
      } catch (cause) {
        error.textContent = cause.message || 'Your reply could not be sent.';
        state.textContent = '';
      } finally {
        button.disabled = false;
      }
    });

    loadTicket().catch((cause) => {
      portal.querySelector('[data-ticket-reference]').textContent = credentials.ticket;
      state.textContent = cause.message || 'This private link is no longer valid.';
      reply.hidden = true;
    });
  }

  function initSupport(root) {
    if (portalCredentials) {
      initTicketPortal(root, portalCredentials);
      return;
    }
    if (root.dataset.supportReady === 'true') return;
    root.dataset.supportReady = 'true';
    const form = root.querySelector('[data-support-form]');
    const status = root.querySelector('[data-support-status]');
    const message = root.querySelector('[data-support-message]');
    const count = root.querySelector('[data-support-count]');
    const startedAt = root.querySelector('[data-support-started-at]');
    const apiBase = root.dataset.apiBase || '';
    startedAt.value = String(Date.now());
    message.addEventListener('input', () => { count.textContent = `${message.value.length} / 5000`; });

    function clearErrors() {
      root.querySelectorAll('[data-support-error]').forEach((node) => { node.textContent = ''; });
      form.querySelectorAll('[aria-invalid="true"]').forEach((node) => node.removeAttribute('aria-invalid'));
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearErrors();
      status.textContent = 'Submitting your ticket…';
      const button = form.querySelector('button[type="submit"]');
      button.disabled = true;
      const data = Object.fromEntries(new FormData(form).entries());
      data.requestId = requestId();
      data.startedAt = Number(data.startedAt);
      try {
        const response = await fetch(`${apiBase}/api/tickets`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify(data),
        });
        const result = await response.json();
        if (!response.ok) {
          showFormErrors(root, form, result.errors || { form: result.error || 'We could not submit your ticket. Please try again.' });
          status.textContent = '';
          return;
        }
        form.hidden = true;
        const success = root.querySelector('[data-support-success]');
        success.hidden = false;
        success.querySelector('[data-support-reference]').textContent = result.ticket.id;
        success.querySelector('[data-support-tracking]').href = result.trackingUrl;
        if (result.notification === 'sent') success.querySelector('[data-support-notice]').textContent = `Your ticket ${result.ticket.id} is safely stored. We’ve also emailed your private tracking link.`;
        success.focus();
      } catch {
        showFormErrors(root, form, { form: 'We could not reach support. Check your connection and try again.' });
        status.textContent = '';
      } finally {
        button.disabled = false;
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-native-support]').forEach(initSupport);
  }
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('mintrove:render-path', initAll);
  initAll();
})();
